
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useThematicTrails } from '@/hooks/useNodes';
import { Eye, Edit, Trash2, Route } from 'lucide-react';

interface TrailsListProps {
  onEditTrail?: (trailId: string) => void;
}

const TrailsList: React.FC<TrailsListProps> = ({ onEditTrail }) => {
  const { data: trails } = useThematicTrails();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'bg-white text-green-800';
      case 'draft': return 'bg-white text-black';
      case 'in_review': return 'bg-white text-black';
      default: return 'bg-white text-black';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Route className="h-5 w-5" />
          Existing Thematic Trails ({trails?.length || 0})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!trails || trails.length === 0 ? (
          <p className="text-black text-center py-8">
            No thematic trails created yet
          </p>
        ) : (
          <div className="space-y-4">
            {trails.map((trail) => (
              <div key={trail.id} className="p-4 border rounded-lg hover:bg-muted/50">
                <div className="flex items-center justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{trail.title}</h3>
                      <Badge className={getStatusColor(trail.status)}>
                        {trail.status}
                      </Badge>
                    </div>
                    {trail.description && (
                      <p className="text-sm text-black">{trail.description}</p>
                    )}
                    <div className="flex gap-2 text-xs text-black">
                      <span>Created: {new Date(trail.created_at).toLocaleDateString()}</span>
                      <span>•</span>
                      <span>Updated: {new Date(trail.updated_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline">
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => onEditTrail?.(trail.id)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button size="sm" variant="destructive">
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TrailsList;
